import { useCallback, useEffect, useMemo, useState } from 'react';
import type { DynamicOption } from '@refineintel/types';
import { host } from '@branding/config';
import { errorHandler } from '@/Config/ErrorHandler';
import { useThrottledValue } from './useThrottledValue';

/**
 * v2 (HRR) server-backed option fetcher for the searchable AutocompleteElement
 * (RND-4221 / RND-4269).
 *
 * Builds its base the canonical way the rest of the v2 PageStep flow does
 * (`useQueryAnswerPage` — page / answerPage / goBack): `${host}/api`, where
 * `host` comes from `@branding/config` (`VITE_BACKEND_ENDPOINT`). This reaches
 * the exact origin every other v2 call already proves reachable
 * (`${host}/api/getDynamicOptions`) — e.g. on valley-stg,
 * `https://api.valley.stg.refineintelligence.com/playbookquestionnaire/api/getDynamicOptions?type=naics`
 * returns 2125 options. Keeping the base identical to `useQueryAnswerPage`
 * prevents the two from drifting apart (proxy / CORS origin / gateway path).
 *
 * Behaviour mirrors v1 `useQueryGetDynamicOptions`:
 *  - `searchQuery` is debounced 500ms via `useThrottledValue`.
 *  - page 1 replaces the list; subsequent pages append (infinite scroll).
 *  - `hasMorePages = currentPage < totalPages`.
 */

interface PaginationState {
	totalCount: number;
	totalPages: number;
	currentPage: number;
	pageSize: number;
}

interface DynamicOptionsApiResponse {
	status: string;
	message?: string;
	data: DynamicOption[];
	pagination: PaginationState;
}

export function useV2DynamicOptions({
	searchQuery,
	type,
	pagination,
	lang,
}: {
	searchQuery: string;
	type: string;
	pagination: { pageSize: number; currentPage: number };
	lang?: string;
}) {
	const base = useMemo(() => `${host}/api`, []);
	const [options, setOptions] = useState<DynamicOption[]>([]);
	const [isLoading, setIsLoading] = useState(false);
	const [paginationInfo, setPaginationInfo] = useState<PaginationState>({
		totalCount: 0,
		totalPages: 0,
		currentPage: 1,
		pageSize: pagination.pageSize,
	});

	const throttledSearchQuery = useThrottledValue(searchQuery, 500);

	const fetchOptions = useCallback(
		async (controller: AbortController) => {
			setIsLoading(true);
			try {
				const params = new URLSearchParams({
					// Send the element's `dynamicOptionsType` verbatim (e.g. "naics").
					type,
					// REQUIRED by the BE schema; "" for the initial page.
					searchQuery: throttledSearchQuery,
					currentPage: pagination.currentPage.toString(),
					pageSize: pagination.pageSize.toString(),
				});
				if (lang) params.set('lang', lang);

				const res = await fetch(`${base}/getDynamicOptions?${params.toString()}`, {
					method: 'GET',
					headers: { Accept: 'application/json' },
					signal: controller.signal,
				});
				if (!res.ok) throw new Error(`HTTP error! status: ${res.status}`);

				const resData = (await res.json()) as DynamicOptionsApiResponse;
				if (resData.status === 'error') throw new Error(resData.message ?? 'getDynamicOptions failed');

				setOptions((prev) =>
					pagination.currentPage === 1 ? resData.data : [...prev, ...resData.data],
				);
				if (resData.pagination) setPaginationInfo(resData.pagination);
			} catch (err) {
				if ((err as { name?: string })?.name !== 'AbortError') {
					// Non-fatal: leave the existing list in place; the dropdown shows
					// "no results" rather than crashing the page. Report via Bugsnag
					// (errorHandler) to match v1 useQueryGetDynamicOptions telemetry.
					errorHandler(new Error('Failed to fetch dynamic options'), {
						action: 'fetchOptions',
						context: {
							searchQuery: throttledSearchQuery,
							type,
							page: pagination.currentPage,
							pageSize: pagination.pageSize,
						},
						originalError: err,
						statusCode: err instanceof Error && 'status' in err ? (err as { status?: number }).status : undefined,
					});
				}
			} finally {
				setIsLoading(false);
			}
		},
		[base, throttledSearchQuery, type, pagination.currentPage, pagination.pageSize, lang],
	);

	useEffect(() => {
		const controller = new AbortController();
		void fetchOptions(controller);
		return () => controller.abort();
	}, [fetchOptions]);

	return {
		options,
		isLoading,
		hasMorePages: pagination.currentPage < paginationInfo.totalPages,
		paginationInfo,
	};
}
